import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import Modal from 'components/Luxkit/Modal/Modal'
import { copyValueToClipboard, copyAvailable } from 'lib/web/clipboardUtils'
import { offerShareButtonClick, offerShareComplete } from 'actions/OfferActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import * as Analytics from 'analytics/analytics'
import ShareByEmailForm from './ShareByEmailForm'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import Group from 'components/utils/Group'
import ShareThis, { ShareOption } from 'components/Vendor/ShareThis'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineEnvelopeIcon from 'components/Luxkit/Icons/line/LineEnvelopeIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineLinkIcon from 'components/Luxkit/Icons/line/LineLinkIcon'
import styled from 'styled-components'
import { rem } from 'polished'

export const EMAIL_MODE = 'email'

const ShareLinkTextContainer = styled.div`
  cursor: pointer;
  background: ${props => props.theme.palette.neutral.default.six};
  overflow: hidden;
  padding: ${rem(12)};
`

const CopyLinkOuterContainer = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  overflow: hidden;
`

export interface ShareConfig {
  url?: string;
  customerPortalURL?: string;
  memberId?: string;
  brand?: string;
  holidaySynonym?: string;
  image?: App.Image;
  offerId?: string;
  offerLocation?: string;
  pathName?: string;
  region?: string;
  type?: string;
  utmCampaign?: string;
  shareTitle?: string;
  mailToSubject?: string;
  pageId?: string;
  pageType?: string;
  onSend?: (state: { friend_email: string, message?: string }) => Promise<any>;
  shareMessage?: string;
  mailToObject?: string;
}

interface Props {
  loggedIn?: boolean;
  shareConfig: ShareConfig;
  children?: React.ReactElement;
  offerShareButtonClick: (...params: Parameters<typeof offerShareButtonClick>) => void;
  offerShareComplete: (...params: Parameters<typeof offerShareComplete>) => void;
  onShare?: (network: string) => void;
  mode?: typeof EMAIL_MODE;
}

function ShareModal(props: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<typeof EMAIL_MODE | undefined>(props.mode)

  const openShareModal = () => {
    const { offerShareButtonClick, shareConfig } = props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareButtonClick(
        shareConfig.pageId,
        shareConfig.pageType,
      )
    }
    setIsOpen(true)
  }

  const onShare = (shareMethod: string, shareMethodKey?: string, closeModal: boolean = true) => {
    const { offerShareComplete, shareConfig, onShare } = props
    if (shareConfig.pageId && shareConfig.pageType) {
      offerShareComplete(
        shareConfig.pageId,
        shareConfig.pageType,
        shareMethod,
      )
    }
    Analytics.trackClientEvent({
      subject: 'share',
      action: shareMethodKey ?? shareMethod,
      category: 'offer_page',
      type: 'interaction',
    })
    onShare?.(shareMethod)
    if (closeModal) closeShareModal()
  }

  const closeShareModal = () => {
    setIsOpen(false)
    setMode(undefined)
  }

  const mailTo = useMemo(() => {
    if (props.loggedIn) return
    if (!props.shareConfig.url) return
    const body = encodeURIComponent(props.shareConfig.url)
    const subject = props.shareConfig.mailToSubject
    return `mailto:?subject=${subject}&body=${body}`
  }, [props.loggedIn, props.shareConfig.mailToSubject, props.shareConfig.url])

  const copyToClipboard = () => {
    if (props.shareConfig.url) {
      copyValueToClipboard(props.shareConfig.url)
      showSnackbar('Link copied to clipboard', 'success')
    }
    onShare('clipboard', undefined, false)
  }

  const {
    shareConfig,
    children,
    loggedIn,
  } = props

  const buttonChild = React.Children.only(children) as React.ReactElement<{ onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }>
  const ShareButton = buttonChild ? React.cloneElement(buttonChild, {
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      const originalOnClick = buttonChild.props.onClick
      if (originalOnClick) {
        originalOnClick(e)
      }
      openShareModal()
    },
  }) : null

  const modalTitle = useMemo(() => `Share this ${shareConfig.pageType}`, [shareConfig.pageType])

  const shareMessage = shareConfig.shareMessage || ''

  return (
    <>
      {ShareButton}
      <Modal
        title={modalTitle}
        isOpen={isOpen}
        onClose={closeShareModal}
      >
        <>
          {mode === EMAIL_MODE && (<ShareByEmailForm
            onShare={onShare}
            closeShareModal={closeShareModal}
            shareMessage={shareMessage}
            shareConfig={shareConfig}
          />)}
          {mode !== EMAIL_MODE && (
            <Group direction="vertical" gap={16}>
              <div>
                <ShareThis onShare={onShare} shareConfig={shareConfig}/>
                <ShareOption
                  kind="tertiary"
                  href={mailTo}
                  onClick={() => {
                    loggedIn ? setMode?.(EMAIL_MODE) : onShare('email')
                  }}
                  startIcon={<LineEnvelopeIcon />}
                >
                  Email
                </ShareOption>
              </div>
              {copyAvailable() && (<CopyLinkOuterContainer>
                <Group direction="horizontal" gap={0} verticalAlign="center" >
                  <ShareLinkTextContainer onClick={copyToClipboard}>
                    <BodyText variant="small" wrap="truncate" colour="neutral-three">
                      {props.shareConfig.url?.replace('https://', '')}
                    </BodyText>
                  </ShareLinkTextContainer>
                  <TextButton kind="tertiary" onClick={copyToClipboard} startIcon={<LineLinkIcon />}>
                    Copy link
                  </TextButton>
                </Group>
              </CopyLinkOuterContainer>
              )}
            </Group>
          )}
        </>
      </Modal>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    loggedIn: selectLoggedIn(state),
  }
}

const actions = {
  offerShareButtonClick,
  offerShareComplete,
}

export default connect(mapStateToProps, actions)(ShareModal)
